import type { TFunction } from 'i18next';

/** Calendar age of an aquarium: whole years, then whole months, then days. */
export interface AquariumAge {
  years: number;
  months: number;
  days: number;
}

/**
 * The local calendar date of an ISO date ("2022-07-16") or timestamp. A plain
 * date is read as that day, not as UTC midnight (which is the day before west
 * of Greenwich).
 */
function calendarDate(value: string): Date | null {
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = plain
    ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
    : new Date(value);
  if (isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Age since `since` (the setup date, else the creation date) on `now`, by the
 * calendar: 16.07.2022 → 18.10.2026 is 4 years, 3 months, 2 days. Null for a
 * missing or invalid date or one in the future.
 */
export function aquariumAge(since: string | null | undefined, now: Date = new Date()): AquariumAge | null {
  if (!since) return null;
  const start = calendarDate(since);
  if (!start) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (start > today) return null;

  let months = (today.getFullYear() - start.getFullYear()) * 12 + today.getMonth() - start.getMonth();
  if (today.getDate() < start.getDate()) months -= 1;
  // The date `months` months after start (clamped to the month's last day, e.g. 31 Jan + 1 month → 28/29 Feb).
  const anchorMonth = start.getMonth() + months;
  const lastDay = new Date(start.getFullYear(), anchorMonth + 1, 0).getDate();
  const anchor = new Date(start.getFullYear(), anchorMonth, Math.min(start.getDate(), lastDay));
  const days = Math.round((today.getTime() - anchor.getTime()) / 86_400_000);
  return { years: Math.floor(months / 12), months: months % 12, days };
}

/**
 * Short display text of an aquarium's age: days below one month, months below
 * one year, else years (and months, when there are any). Empty when unknown.
 */
export function formatAquariumAge(since: string | null | undefined, t: TFunction, now: Date = new Date()): string {
  const age = aquariumAge(since, now);
  if (!age) return '';
  if (age.years === 0 && age.months === 0) return t('aquarium.ageValue.days', { count: age.days });
  if (age.years === 0) return t('aquarium.ageValue.months', { count: age.months });
  const years = t('aquarium.ageValue.years', { count: age.years });
  if (age.months === 0) return years;
  return t('aquarium.ageValue.yearsMonths', { years, months: t('aquarium.ageValue.months', { count: age.months }) });
}
